'use client';
import React from 'react';

interface Service {
  id: string;
  tag: string;
  title: string;
  description: string;
  features: string[];
  icon: React.ReactNode;
}

const services: Service[] = [
  {
    id: 'bookkeeping',
    tag: 'Core Service',
    title: 'Bookkeeping',
    description:
      'Accurate, up-to-date records of every financial transaction. I maintain your general ledger, categorize expenses, and keep your books audit-ready at all times.',
    features: [
      'Monthly transaction recording',
      'Expense categorization',
      'General ledger maintenance',
      'Accounts payable & receivable tracking',
      'Monthly closing entries',
    ],
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <rect x="4" y="4" width="24" height="28" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <path d="M9 12h14M9 17h14M9 22h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'reconciliation',
    tag: 'Core Service',
    title: 'Bank Reconciliation',
    description:
      'Systematic matching of your bank statements against your internal records to catch discrepancies, prevent fraud, and ensure financial accuracy every month.',
    features: [
      'Monthly bank statement matching',
      'Discrepancy identification & resolution',
      'Outstanding check tracking',
      'Deposit-in-transit reconciliation',
      'Reconciliation reports',
    ],
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <path d="M4 16h24M16 4v24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="16" cy="16" r="10" stroke="currentColor" strokeWidth="1.5" />
        <path d="M11 16l3 3 7-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'financial-statements',
    tag: 'Core Service',
    title: 'Financial Statement Preparation',
    description:
      'Professionally prepared income statements, balance sheets, and cash flow statements that give you a clear picture of your business performance and financial health.',
    features: [
      'Income statement (P&L)',
      'Balance sheet preparation',
      'Cash flow statement',
      'Notes and disclosures',
      'Comparative period analysis',
    ],
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <path d="M6 26V12l10-8 10 8v14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="12" y="18" width="8" height="8" stroke="currentColor" strokeWidth="1.5" />
        <path d="M10 16h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
];

export default function ServicesSection() {
  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" className="bg-brand-grey50 py-16 sm:py-24 lg:py-32">
      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-12">
        {/* Section header */}
        <div className="mb-10 sm:mb-16">
          <p
            className="text-primary font-sans text-xs font-semibold tracking-widest uppercase mb-4"
            style={{ letterSpacing: '3px' }}
          >
            What I offer
          </p>
          <h2
            className="section-heading mb-4 sm:mb-6"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(28px, 6vw, 52px)', fontWeight: 700, letterSpacing: '-1px', color: '#111111' }}
          >
            Services built for
            <br />
            growing businesses.
          </h2>
          <p
            className="text-brand-grey700 text-base sm:text-lg max-w-xl"
            style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", lineHeight: '1.7' }}
          >
            Three focused financial services — each delivered with the precision and methodology of a CMA-trained professional.
          </p>
        </div>

        {/* Service cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
          {services.map((service, index) => (
            <div
              key={service.id}
              className="group relative bg-brand-black text-white p-6 sm:p-8 lg:p-10 transition-all duration-500 cursor-default"
              style={{
                transitionTimingFunction: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
                boxShadow: 'rgba(10, 10, 11, 0.5) 0px 18px 50px -22px',
                borderRight: index < services.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                borderBottom: index < services.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
              }}
            >
              {/* Top orange bar */}
              <div className="w-10 h-0.5 bg-primary mb-6 sm:mb-8 transition-all duration-300 group-hover:w-16" />

              {/* Tag */}
              <p
                className="text-primary text-xs font-semibold tracking-widest uppercase mb-3 sm:mb-4"
                style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", letterSpacing: '2px' }}
              >
                {service.tag}
              </p>

              {/* Icon */}
              <div className="text-white/60 mb-5 sm:mb-6 group-hover:text-primary transition-colors duration-300">
                {service.icon}
              </div>

              {/* Title */}
              <h3
                className="text-white mb-3 sm:mb-4"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 'clamp(20px, 4vw, 26px)',
                  fontWeight: 600,
                  lineHeight: '1.25',
                  letterSpacing: '-0.5px',
                }}
              >
                {service.title}
              </h3>

              {/* Description */}
              <p
                className="text-white/65 text-sm sm:text-base mb-6 sm:mb-8 leading-relaxed"
                style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", lineHeight: '1.7' }}
              >
                {service.description}
              </p>

              {/* Features */}
              <ul className="space-y-2 mb-8 sm:mb-10">
                {service.features.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-start gap-3 text-white/55 text-sm"
                    style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                  >
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={() => handleScroll('#estimate')}
                className="text-white font-bold text-sm border-b border-primary pb-0.5 transition-all duration-300 hover:border-b-2 bg-transparent cursor-pointer touch-manipulation"
                style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", minHeight: '44px', display: 'inline-flex', alignItems: 'center' }}
              >
                Get an estimate →
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
